import React from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

/**
 * Higher-order component that provides React Router v6 navigation capabilities to class components
 * 
 * This HOC wraps a component and injects the following props:
 * - navigate: Function from useNavigate hook
 * - location: Object from useLocation hook
 * - params: Object from useParams hook
 * - history: Object with push, replace, and goBack methods (for backward compatibility)
 * 
 * Usage:
 * ```
 * class MyComponent extends React.Component {
 *   componentDidMount() {
 *     // Use this.props.navigate or this.props.history.push
 *   }
 * }
 * 
 * export default withNavigation(MyComponent);
 * ```
 */
export function withNavigation(Component) {
  return function NavigationWrapper(props) {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();

    // Create a history object that mimics the React Router v5 history object
    const history = {
      push: (path, state) => {
        navigate(path, { state });
      },
      replace: (path, state) => {
        navigate(path, { replace: true, state });
      },
      goBack: () => {
        navigate(-1);
      },
      location
    };

    return <Component {...props} navigate={navigate} location={location} params={params} history={history} />;
  };
}

export default withNavigation;
